import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { BarProps } from 'recharts';
import { type CSSProperties, type FC, useMemo } from 'react';
import { Text } from '@shared/ui/antd';
import styles from '../analytics.module.scss';
import type { ChartPoint } from '../lib/helpers';
import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@shared/redux';
import { appSettingsSelectors } from '@shared/redux/ducks/app-settings';
import { AppTheme } from '@shared/services/theme';

type Props = {
  title: string;
  total: number;
  data: ChartPoint[];
};

const BAR_FILL_LIGHT = '#F0F6FF';
const BAR_FILL_DARK = 'rgba(115, 151, 243, 0.35)';
const BAR_STRIPE_LIGHT = '#234EEE';
const BAR_STRIPE_DARK = '#7397F3';
const BAR_RADIUS = 8;
const BAR_STRIPE_HEIGHT = 2;
const BAR_BG_FILL_LIGHT = '#F3F3F3';
const BAR_BG_FILL_DARK = '#2A2A2A';
const LABEL_FILL_LIGHT = '#1C1C1C';
const LABEL_FILL_DARK = 'rgba(255, 255, 255, 0.88)';
const AXIS_TICK_FILL_LIGHT = 'rgba(0, 0, 0, 0.45)';
const AXIS_TICK_FILL_DARK = 'rgba(255, 255, 255, 0.55)';

const buildBottomRoundedPath = (x: number, y: number, width: number, height: number) => {
  const radius = Math.max(0, Math.min(BAR_RADIUS, width / 2, height / 2));
  const right = x + width;
  const bottom = y + height;
  const bottomStart = bottom - radius;

  return [
    `M ${x} ${y}`,
    `H ${right}`,
    `V ${bottomStart}`,
    `Q ${right} ${bottom} ${right - radius} ${bottom}`,
    `H ${x + radius}`,
    `Q ${x} ${bottom} ${x} ${bottomStart}`,
    'Z',
  ].join(' ');
};

const createBarShape = (fill: string, stripe: string) => (props: unknown) => {
  const { x, y, width, height } = props as BarProps;
  const safeX = Number(x ?? 0);
  const safeY = Number(y ?? 0);
  const safeWidth = Number(width ?? 0);
  const safeHeight = Number(height ?? 0);
  if (safeHeight <= 0 || safeWidth <= 0) return <g />;

  const stripeHeight = Math.min(BAR_STRIPE_HEIGHT, safeHeight);
  const path = buildBottomRoundedPath(safeX, safeY, safeWidth, safeHeight);

  return (
    <g>
      <path d={path} fill={fill} />
      <rect x={safeX} y={safeY} width={safeWidth} height={stripeHeight} fill={stripe} />
    </g>
  );
};

const createBarBackgroundShape = (fill: string) => (props: unknown) => {
  const { x, y, width, height } = props as BarProps;
  const safeX = Number(x ?? 0);
  const safeY = Number(y ?? 0);
  const safeWidth = Number(width ?? 0);
  const safeHeight = Number(height ?? 0);
  if (safeHeight <= 0 || safeWidth <= 0) return <g />;

  return (
    <rect
      x={safeX}
      y={safeY}
      width={safeWidth}
      height={safeHeight}
      rx={BAR_RADIUS}
      ry={BAR_RADIUS}
      fill={fill}
    />
  );
};

export const AnalyticsChartCard: FC<Props> = ({ title, total, data }) => {
  const { t } = useTranslation('translation');
  const themeMode = useAppSelector(appSettingsSelectors.selectTheme);
  const isDark = themeMode === AppTheme.dark;
  const barFill = isDark ? BAR_FILL_DARK : BAR_FILL_LIGHT;
  const barStripe = isDark ? BAR_STRIPE_DARK : BAR_STRIPE_LIGHT;
  const barBgFill = isDark ? BAR_BG_FILL_DARK : BAR_BG_FILL_LIGHT;
  const labelFill = isDark ? LABEL_FILL_DARK : LABEL_FILL_LIGHT;
  const axisTickFill = isDark ? AXIS_TICK_FILL_DARK : AXIS_TICK_FILL_LIGHT;
  const tooltipContentStyle: CSSProperties = isDark
    ? {
        background: '#1F232C',
        border: '1px solid rgba(255, 255, 255, 0.16)',
        borderRadius: 8,
        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.42)',
      }
    : {
        background: '#FFFFFF',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: 8,
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
      };
  const tooltipLabelStyle: CSSProperties = {
    color: isDark ? 'rgba(255, 255, 255, 0.88)' : 'rgba(0, 0, 0, 0.88)',
    marginBottom: 4,
  };
  const tooltipItemStyle: CSSProperties = {
    color: isDark ? 'rgba(255, 255, 255, 0.72)' : 'rgba(0, 0, 0, 0.72)',
  };
  const barShape = useMemo(() => createBarShape(barFill, barStripe), [barFill, barStripe]);
  const barBackgroundShape = useMemo(() => createBarBackgroundShape(barBgFill), [barBgFill]);
  const valueLabelGutter = useMemo(() => {
    const longestValue = data.reduce(
      (longest, point) => Math.max(longest, String(point.value).length),
      1,
    );

    return Math.max(8, Math.ceil(longestValue * 4));
  }, [data]);

  return (
    <Flex vertical>
      <Text variant="xl" fontWeight={500}>
        {title}
      </Text>

      <div className={styles.chartTotal}>{total}</div>

      <div className={styles.chart}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 24,
              right: valueLabelGutter,
              left: valueLabelGutter,
              bottom: 8,
            }}
          >
            <XAxis
              dataKey="name"
              padding={{ left: 0, right: 0 }}
              axisLine={false}
              tickLine={false}
              tick={{ fill: axisTickFill }}
            />

            <YAxis
              hide
              domain={[
                0,
                (max: number) => {
                  const safeMax = Number.isFinite(max) ? max : 0;
                  if (safeMax <= 0) return 1;
                  return safeMax + Math.max(1, Math.ceil(safeMax * 0.1));
                },
              ]}
            />

            <Tooltip
              formatter={(value) => [value, t('analytics.tooltip.countLabel')]}
              isAnimationActive={false}
              contentStyle={tooltipContentStyle}
              labelStyle={tooltipLabelStyle}
              itemStyle={tooltipItemStyle}
              cursor={false}
            />

            <Bar
              dataKey="value"
              isAnimationActive={false}
              activeBar={false}
              shape={barShape}
              background={barBackgroundShape}
              minPointSize={10}
            >
              <LabelList
                dataKey="value"
                position="top"
                offset={6}
                fill={labelFill}
                fontSize={12}
                fontWeight={500}
                formatter={(value: unknown) =>
                  Number(value) === 0 || value == null || Number.isNaN(Number(value))
                    ? ''
                    : String(value)
                }
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Flex>
  );
};
